import { Navigate, Outlet, Route, Routes } from "react-router-dom";
import { MarketingLayout } from "./components/MarketingLayout";
import { StudioLayout } from "./components/StudioLayout";
import { SopGuard } from "./components/SopGuard";
import { SopDashboard, SopProcedurePage, SopVersionPage, SopRunPage } from "./pages/sop-ai";
import { Home } from "./pages/marketing/Home";
import { LegacyStudioRedirect } from "./components/LegacyStudioRedirect";
import { Marketplace } from "./pages/marketing/Marketplace";
import { MarketplaceApp } from "./pages/marketing/MarketplaceApp";
import { MarketplaceBundle } from "./pages/marketing/MarketplaceBundle";
import { MarketplaceUpdates } from "./pages/marketing/MarketplaceUpdates";
import { ContractAIMarketing } from "./pages/marketing/ContractAIMarketing";
import { ExternalRedirect } from "./components/ExternalRedirect";
import { TRACELOCK_URL } from "./components/MarketingLayout";
import { Pricing } from "./pages/marketing/Pricing";
import { Solutions } from "./pages/marketing/Solutions";
import { Security } from "./pages/marketing/Security";
import { Resources } from "./pages/marketing/Resources";
import { About } from "./pages/marketing/About";
import { Customers } from "./pages/marketing/Customers";
import { Contact } from "./pages/marketing/Contact";
import { Login } from "./pages/auth/Login";
import { StartTrial } from "./pages/auth/StartTrial";
import { Redeem } from "./pages/auth/Redeem";
import { Verify } from "./pages/auth/Verify";
import { ForgotPassword } from "./pages/auth/ForgotPassword";
import { ResetPassword } from "./pages/auth/ResetPassword";
import { AcceptInvite } from "./pages/auth/AcceptInvite";
import { Checkout } from "./pages/auth/Checkout";
import { StudioHome } from "./pages/studio/StudioHome";
import { AttentionCenter } from "./pages/studio/AttentionCenter";
import { ContextBridge } from "./pages/studio/ContextBridge";
import { MyApplications } from "./pages/studio/MyApplications";
import { Trials } from "./pages/studio/Trials";
import { Billing } from "./pages/studio/Billing";
import { Usage } from "./pages/studio/Usage";
import { Seats } from "./pages/studio/Seats";
import { OrganizationPage } from "./pages/studio/OrganizationPage";
import { Users } from "./pages/studio/Users";
import { Settings } from "./pages/studio/Settings";
import { Support } from "./pages/studio/Support";
import { Notifications } from "./pages/studio/Notifications";
import { Documents } from "./pages/studio/Documents";
import { CaiDashboard } from "./pages/contract-ai/CaiDashboard";
import { CaiRepository } from "./pages/contract-ai/CaiRepository";
import { CaiUpload } from "./pages/contract-ai/CaiUpload";
import { CaiContractDetail } from "./pages/contract-ai/CaiContractDetail";
import { CaiReview } from "./pages/contract-ai/CaiReview";
import { CaiAsk } from "./pages/contract-ai/CaiAsk";
import { CaiClauses } from "./pages/contract-ai/CaiClauses";
import { CaiObligations } from "./pages/contract-ai/CaiObligations";
import { CaiCompliance } from "./pages/contract-ai/CaiCompliance";
import { ProposalGuard } from "./components/ProposalGuard";
import { PaiDashboard } from "./pages/proposal-ai/PaiDashboard";
import { PaiOpportunities } from "./pages/proposal-ai/PaiOpportunities";
import { PaiOpportunityDetail } from "./pages/proposal-ai/PaiOpportunityDetail";
import { PaiSolicitation } from "./pages/proposal-ai/PaiSolicitation";
import { PaiWorkspaceDetail } from "./pages/proposal-ai/PaiWorkspaceDetail";
import { PaiAsk } from "./pages/proposal-ai/PaiAsk";
import { PaiCompliance } from "./pages/proposal-ai/PaiCompliance";
import { PaiWorkspace } from "./pages/proposal-ai/PaiWorkspace";
import { PaiKnowledge } from "./pages/proposal-ai/PaiKnowledge";
import { PaiReviews } from "./pages/proposal-ai/PaiReviews";
import { PaiSubmission } from "./pages/proposal-ai/PaiSubmission";
import { PaiConnected } from "./pages/proposal-ai/PaiConnected";
import { PaiAnalytics } from "./pages/proposal-ai/PaiAnalytics";
import { PaiSettings } from "./pages/proposal-ai/PaiSettings";
import { PlatformAdminGuard } from "./components/PlatformAdminGuard";
import { CatalogAdmin } from "./pages/admin/CatalogAdmin";
import { AdminApplication } from "./pages/admin/AdminApplication";
import { AdminBundle } from "./pages/admin/AdminBundle";
import { PolicyGuard } from "./components/PolicyGuard";
import { PolDashboard } from "./pages/policy-ai/PolDashboard";
import { PolDrafts } from "./pages/policy-ai/PolDrafts";
import { PolDraftDetail } from "./pages/policy-ai/PolDraftDetail";
import { PolAcknowledgments } from "./pages/policy-ai/PolAcknowledgments";
import { PolExceptions } from "./pages/policy-ai/PolExceptions";
import { PolExternalReview } from "./pages/policy-ai/PolExternalReview";
import { VenDashboard } from "./pages/vendor-ai/VenDashboard";
import { VenVendors } from "./pages/vendor-ai/VenVendors";
import { VenVendorDetail } from "./pages/vendor-ai/VenVendorDetail";
import { VenIntake } from "./pages/vendor-ai/VenIntake";
import { VenAssessments } from "./pages/vendor-ai/VenAssessments";
import { VenAssessmentDetail } from "./pages/vendor-ai/VenAssessmentDetail";
import { VenFindings } from "./pages/vendor-ai/VenFindings";
import { VendorGuard } from "./components/VendorGuard";
import { ReviewGuard } from "./components/ReviewGuard";
import { ReviewDashboard } from "./pages/review-ai/ReviewDashboard";
import { ReviewDetail } from "./pages/review-ai/ReviewDetail";
import { ReviewLens } from "./pages/review-ai/ReviewLens";
import { MeetingGuard } from "./components/MeetingGuard";
import { MeetingHome, MeetingDetail, CommitmentList, CommitmentDetail, MeetingWorkItems } from "./pages/meeting-ai/MeetingPages";
import { CalendarGuard } from "./components/CalendarGuard";
import {
  CalendarAiLanding,
  CalendarAiSchedules,
  CalendarAiScheduleDetail,
  CalendarAiDates,
  CalendarAiDateDetail,
  CalendarAiBindings,
  CalendarAiReconcile,
  CalendarAiReflectedDates,
} from "./pages/calendar-ai/CalendarAi";
import { GovernanceGuard } from "./components/GovernanceGuard";
import { GovOverview } from "./pages/governance/GovOverview";
import { GovFrameworks } from "./pages/governance/GovFrameworks";
import { GovFrameworkVersion } from "./pages/governance/GovFrameworkVersion";
import { GovMappings } from "./pages/governance/GovMappings";
import { GovEvidence } from "./pages/governance/GovEvidence";
import { GovEvidenceItem } from "./pages/governance/GovEvidenceItem";
import { GovEvidenceRequests } from "./pages/governance/GovEvidenceRequests";
import { GovEvidencePackages } from "./pages/governance/GovEvidencePackages";
import { GovExceptions } from "./pages/governance/GovExceptions";
import { GovExceptionDetail } from "./pages/governance/GovExceptionDetail";
import { GovRegulations } from "./pages/governance/GovRegulations";
import { GovRegulationDetail } from "./pages/governance/GovRegulationDetail";
import { GovImpact } from "./pages/governance/GovImpact";
import { GovRemediation } from "./pages/governance/GovRemediation";
import { GovAudits } from "./pages/governance/GovAudits";
import { GovAuditDetail } from "./pages/governance/GovAuditDetail";
import { GovReports } from "./pages/governance/GovReports";
import { GovReportRun } from "./pages/governance/GovReportRun";
import { GovernancePermissionProvider } from "./governance";

export function App() {
  return (
    <Routes>
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Home />} />
        {/* The Marketplace is the single catalog browser; /products was a second,
            hand-maintained storefront. */}
        <Route path="/products" element={<Navigate to="/marketplace" replace />} />
        {/* Short vanity routes are canonical; the old /products/* paths redirect. */}
        {/* TraceLock is a separate product with its own workspace and
            subscription, so it is sold from the corporate site, not here. */}
        <Route path="/tracelock" element={<ExternalRedirect to={TRACELOCK_URL} />} />
        <Route path="/contract-ai" element={<ContractAIMarketing />} />
        <Route path="/products/tracelock" element={<Navigate to="/tracelock" replace />} />
        <Route path="/products/contract-ai" element={<Navigate to="/contract-ai" replace />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/updates" element={<MarketplaceUpdates />} />
        <Route path="/marketplace/apps/:slug" element={<MarketplaceApp />} />
        <Route path="/marketplace/bundles/:slug" element={<MarketplaceBundle />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/security" element={<Security />} />
        <Route path="/resources" element={<Resources />} />
        <Route path="/about" element={<About />} />
        <Route path="/customers" element={<Customers />} />
        <Route path="/contact" element={<Contact />} />
      </Route>

      <Route path="/login" element={<Login />} />
      <Route path="/start" element={<StartTrial />} />
      <Route path="/redeem" element={<Redeem />} />
      <Route path="/verify" element={<Verify />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/checkout/:sessionId" element={<Checkout />} />
      {/* Token scoped external policy review. No Studio session required. */}
      <Route path="/policy-review" element={<PolExternalReview />} />

      <Route path="/studio" element={<StudioLayout />}>
        <Route index element={<StudioHome />} />
        <Route path="attention" element={<AttentionCenter />} />
        <Route path="context/:subjectType/:subjectId" element={<ContextBridge />} />
        <Route path="my-apps" element={<MyApplications />} />
        <Route path="trials" element={<Trials />} />
        <Route path="billing" element={<Billing />} />
        <Route path="usage" element={<Usage />} />
        <Route path="seats" element={<Seats />} />
        <Route path="organization" element={<OrganizationPage />} />
        <Route path="users" element={<Users />} />
        <Route path="settings" element={<Settings />} />
        <Route path="support" element={<Support />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="documents" element={<Documents />} />

        {/* Meeting AI (APP-005 R1/P6). The catalog remains coming_soon and
            unentitled until browser acceptance; the guard renders that truth. */}
        <Route
          path="meeting-ai"
          element={
            <MeetingGuard>
              <Outlet />
            </MeetingGuard>
          }
        >
          <Route index element={<MeetingHome />} />
          <Route path="meetings/:meetingId" element={<MeetingDetail />} />
          <Route path="commitments" element={<CommitmentList />} />
          <Route path="commitments/:commitmentId" element={<CommitmentDetail />} />
          <Route path="work-items" element={<MeetingWorkItems />} />
        </Route>

        {/* Policy AI */}
        <Route
          path="policy-ai"
          element={
            <PolicyGuard>
              <Outlet />
            </PolicyGuard>
          }
        >
          <Route index element={<PolDashboard />} />
          <Route path="drafts" element={<PolDrafts />} />
          <Route path="drafts/:draftId" element={<PolDraftDetail />} />
          <Route path="acknowledgments" element={<PolAcknowledgments />} />
          <Route path="exceptions" element={<PolExceptions />} />
        </Route>

        {/* Vendor AI (APP-003 R1) */}
        <Route
          path="vendor-ai"
          element={
            <VendorGuard>
              <Outlet />
            </VendorGuard>
          }
        >
          <Route index element={<VenDashboard />} />
          <Route path="vendors" element={<VenVendors />} />
          <Route path="vendors/:vendorId" element={<VenVendorDetail />} />
          <Route path="intake" element={<VenIntake />} />
          <Route path="assessments" element={<VenAssessments />} />
          <Route path="assessments/:assessmentId" element={<VenAssessmentDetail />} />
          <Route path="findings" element={<VenFindings />} />
        </Route>
        <Route
          path="sop-ai"
          element={
            <GovernancePermissionProvider>
              <SopGuard>
                <Outlet />
              </SopGuard>
            </GovernancePermissionProvider>
          }
        >
          <Route index element={<SopDashboard />} />
          <Route path="procedures/:procedureId" element={<SopProcedurePage />} />
          <Route path="procedures/:procedureId/versions/:versionId" element={<SopVersionPage />} />
          <Route path="runs/:runId" element={<SopRunPage />} />
        </Route>

        {/* Calendar AI (APP-007 R1) */}
        <Route
          path="calendar-ai"
          element={
            <CalendarGuard>
              <Outlet />
            </CalendarGuard>
          }
        >
          <Route index element={<CalendarAiLanding />} />
          <Route path="obligation-schedules" element={<CalendarAiSchedules />} />
          <Route path="obligation-schedules/:scheduleId" element={<CalendarAiScheduleDetail />} />
          <Route path="obligated-dates" element={<CalendarAiDates />} />
          <Route path="obligated-dates/:dateId" element={<CalendarAiDateDetail />} />
          <Route path="source-bindings" element={<CalendarAiBindings />} />
          <Route path="source-bindings/:bindingId/reconcile" element={<CalendarAiReconcile />} />
          <Route path="reflected-dates" element={<CalendarAiReflectedDates />} />
        </Route>

        {/* Review AI (APP-006 R1; activation remains catalog-controlled). */}
        <Route
          path="review-ai"
          element={
            <ReviewGuard>
              <Outlet />
            </ReviewGuard>
          }
        >
          <Route index element={<ReviewDashboard />} />
          <Route path="reviews/:reviewId" element={<ReviewDetail />} />
          <Route path="reviews/:reviewId/lenses/:lensId" element={<ReviewLens />} />
        </Route>

        {/* Governance & Compliance (APP-004 R3) */}
        <Route
          path="governance"
          element={
            <GovernanceGuard>
              <GovernancePermissionProvider>
                <Outlet />
              </GovernancePermissionProvider>
            </GovernanceGuard>
          }
        >
          <Route index element={<GovOverview />} />
          <Route path="frameworks" element={<GovFrameworks />} />
          <Route path="frameworks/:versionId" element={<GovFrameworkVersion />} />
          <Route path="mappings" element={<GovMappings />} />
          <Route path="evidence" element={<GovEvidence />} />
          <Route path="evidence/requests" element={<GovEvidenceRequests />} />
          <Route path="evidence/packages" element={<GovEvidencePackages />} />
          <Route path="evidence/items/:itemId" element={<GovEvidenceItem />} />
          <Route path="exceptions" element={<GovExceptions />} />
          <Route path="exceptions/:exceptionId" element={<GovExceptionDetail />} />
          <Route path="regulations" element={<GovRegulations />} />
          <Route path="regulations/:regulationId" element={<GovRegulationDetail />} />
          <Route path="impact" element={<GovImpact />} />
          <Route path="remediation" element={<GovRemediation />} />
          <Route path="audits" element={<GovAudits />} />
          <Route path="audits/:auditProjectId" element={<GovAuditDetail />} />
          <Route path="reports" element={<GovReports />} />
          <Route path="reports/runs/:runId" element={<GovReportRun />} />
        </Route>

        <Route path="contract-ai" element={<CaiDashboard />} />
        <Route path="contract-ai/repository" element={<CaiRepository />} />
        <Route path="contract-ai/upload" element={<CaiUpload />} />
        <Route path="contract-ai/contracts/:contractId" element={<CaiContractDetail />} />
        <Route path="contract-ai/review" element={<CaiReview />} />
        <Route path="contract-ai/ask" element={<CaiAsk />} />
        <Route path="contract-ai/clauses" element={<CaiClauses />} />
        <Route path="contract-ai/obligations" element={<CaiObligations />} />
        <Route path="contract-ai/compliance" element={<CaiCompliance />} />

        <Route path="proposal-ai" element={<ProposalGuard><PaiDashboard /></ProposalGuard>} />
        <Route
          path="proposal-ai/opportunities"
          element={<ProposalGuard entitlement="proposal_ai.opportunity_intelligence"><PaiOpportunities /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/opportunities/:opportunityId"
          element={<ProposalGuard entitlement="proposal_ai.opportunity_intelligence"><PaiOpportunityDetail /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/solicitations/:solicitationId"
          element={<ProposalGuard entitlement="proposal_ai.opportunity_intelligence"><PaiSolicitation /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/ask"
          element={<ProposalGuard entitlement="proposal_ai.opportunity_intelligence"><PaiAsk /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/compliance"
          element={<ProposalGuard entitlement="proposal_ai.government_compliance"><PaiCompliance /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/workspace"
          element={<ProposalGuard entitlement="proposal_ai.authoring"><PaiWorkspace /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/workspace/:workspaceId"
          element={<ProposalGuard entitlement="proposal_ai.authoring"><PaiWorkspaceDetail /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/knowledge"
          element={<ProposalGuard entitlement="proposal_ai.authoring"><PaiKnowledge /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/reviews"
          element={<ProposalGuard entitlement="proposal_ai.government_compliance"><PaiReviews /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/submission"
          element={<ProposalGuard entitlement="proposal_ai.government_compliance"><PaiSubmission /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/connected"
          element={<ProposalGuard entitlement="proposal_ai.connected_intelligence"><PaiConnected /></ProposalGuard>}
        />
        <Route
          path="proposal-ai/analytics"
          element={<ProposalGuard entitlement="proposal_ai.analytics"><PaiAnalytics /></ProposalGuard>}
        />
        <Route path="proposal-ai/settings" element={<ProposalGuard><PaiSettings /></ProposalGuard>} />


        <Route path="catalog-admin" element={<PlatformAdminGuard><CatalogAdmin /></PlatformAdminGuard>} />
        <Route path="catalog-admin/apps/:slug" element={<PlatformAdminGuard><AdminApplication /></PlatformAdminGuard>} />
        <Route path="catalog-admin/bundles/:slug" element={<PlatformAdminGuard><AdminBundle /></PlatformAdminGuard>} />
      </Route>

      {/* Compatibility: the workspace has always lived at /studio. */}
      <Route path="/app/*" element={<LegacyStudioRedirect />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
